/**
 * useVisitorId
 *
 * A stable, anonymous, per-browser identifier used to dedupe unique visitors to
 * program share links and to attribute a later join back to the visit that
 * produced it (view→join conversion).
 *
 * - Minted once and persisted in localStorage under `pcv-visitor-id`.
 * - NOT a user id and carries no PII — just a random UUID.
 * - Privacy-friendly: clearing storage / incognito mints a fresh id (a slight,
 *   accepted over-count of "unique" visitors).
 * - SSR / private-mode safe: every storage access is defensive; if storage is
 *   unavailable we fall back to a session-lived in-memory id so a single page
 *   load is still internally consistent.
 */

const STORAGE_KEY = 'pcv-visitor-id';

// The id this page load answers with, resolved once. Storage is read (and written)
// on the first call only: at tab close, desktop Chrome and the iOS in-app browser
// both handed back EMPTY storage to the unload-time beacon, so a visit's last events
// (landing_dwell, landing_scrolled) were minted a second visitor id (prod, 2026-09-13).
// Also the fallback when storage throws (Safari private mode, SSR).
let resolvedId: string | null = null;

function generateUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // RFC4122-ish v4 fallback (no crypto.randomUUID available).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Test seam: forget the resolved id (a new page load). */
export function __resetVisitorIdForTests(): void {
  resolvedId = null;
}

export function useVisitorId() {
  /**
   * Return the persistent anonymous visitor id, minting + storing one on first
   * call. Never throws.
   */
  const getOrCreateVisitorId = (): string => {
    if (resolvedId) return resolvedId;
    try {
      // A staff preview (?force=...) is never a real visitor: pin the id the shell pins.
      if (typeof window !== 'undefined' && /[?&]force=/.test(window.location.search)) {
        resolvedId = 'qa-preview';
        try {
          window.localStorage.setItem(STORAGE_KEY, resolvedId);
        } catch {
          /* storage blocked */
        }
        return resolvedId;
      }
      if (typeof window !== 'undefined' && window.localStorage) {
        const existing = window.localStorage.getItem(STORAGE_KEY);
        if (existing) {
          resolvedId = existing;
          return existing;
        }
        const fresh = generateUuid();
        window.localStorage.setItem(STORAGE_KEY, fresh);
        resolvedId = fresh;
        return fresh;
      }
    } catch {
      /* storage blocked - mint once below */
    }
    resolvedId = generateUuid();
    return resolvedId;
  };

  return { getOrCreateVisitorId };
}
